import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { createWorkStatus, CreateWorkStatusPayload, CreateWorkStatusResponse } from '../api/createWorkStatus';
import { ApiError } from 'types/apiError';
import { QUERY_KEYS } from 'service/querykeys';

export const useUpdateWorkStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<CreateWorkStatusResponse, ApiError, CreateWorkStatusPayload & { projectUuid: string }>({
    mutationFn: (payload) => createWorkStatus(payload),
    onMutate: async (newStatus) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.PROJECT_DEFAULTS(newStatus.projectUuid) });

      // Snapshot the previous value
      const previousDefaults = queryClient.getQueryData(QUERY_KEYS.PROJECT_DEFAULTS(newStatus.projectUuid));

      // Optimistically update to the new value
      queryClient.setQueryData(QUERY_KEYS.PROJECT_DEFAULTS(newStatus.projectUuid), (old: any) => {
        if (!old || !old.workStatusDTOList) return old;

        return {
          ...old,
          workStatusDTOList: old.workStatusDTOList.map((status: any) =>
            status.workStatusUuid === newStatus.workStatusUuid
              ? { ...status, name: newStatus.name }
              : status
          ),
        };
      });

      // Return a context object with the snapshotted value
      return { previousDefaults, projectUuid: newStatus.projectUuid };
    },
    onError: (error, __, context: any) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousDefaults) {
        queryClient.setQueryData(QUERY_KEYS.PROJECT_DEFAULTS(context.projectUuid), context.previousDefaults);
      }
      toast.error(error?.message || 'Failed to update column');
    },
    onSuccess: () => {
      toast.success('Column updated successfully');
    },
    onSettled: (_, __, variables) => {
      // Always refetch after error or success to handle server-side constraints (like title normalization)
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_DEFAULTS(variables.projectUuid),
      });
    },
  });
};
